import { useEffect, useState } from 'react';
import './Reports.scss';
import { useDispatch, useSelector } from "react-redux";
import { Typography, Collapse, Image, Badge, Button, Spin, Flex, App, Space, Modal } from 'antd';
import ReportsCollapseContent from './ReportsCollapseContent';
import ArrowDownIcon from "../../assets/icons/arrow-down-icon.svg"
import { getReportDropdowns, getJvReports } from '../../redux/slices/ReportsSlice';
import axios from '../../config/httpConfig';
import { EyeOutlined } from '@ant-design/icons';
import {
  downloadBlob,
  getDownloadSuccessMessage,
  getDownloadErrorMessage,
} from '../../helpers/helpers';

const { Title, Text } = Typography;

const JvReportsSection = () => {
  const { message } = App.useApp();
  const { jvReports, jvReportsLoading } = useSelector((state) => state.reports);
  const [downloadingId, setDownloadingId] = useState(null);
  const [viewingId, setViewingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [viewFileUrl, setViewFileUrl] = useState(null);

  useEffect(() => {
    return () => {
      if (viewFileUrl) {
        URL.revokeObjectURL(viewFileUrl);
      }
    };
  }, [viewFileUrl]);

  const handleDownload = async (reportId, reportName) => {
    try {
      setDownloadingId(reportId);
      const blob = await axios.get(`/api/investor/jvreports/download`, {
        params: { reportId },
        responseType: 'blob',
      });

      if (blob?.type === 'application/json') {
        const text = await blob.text();
        const result = JSON.parse(text);
        throw new Error(result?.error || result?.message || 'Download failed');
      }

      const extFromType =
        blob.type === 'application/pdf'
          ? '.pdf'
          : blob.type?.includes('wordprocessingml')
            ? '.docx'
            : blob.type === 'application/msword'
              ? '.doc'
              : '';
      const hasExt = /\.(pdf|docx?)$/i.test(reportName || '');
      const fileName = hasExt ? reportName : `${reportName || 'jv-report'}${extFromType || '.pdf'}`;

      downloadBlob(blob, fileName);
      message.success(getDownloadSuccessMessage(fileName));
    } catch (err) {
      message.error(
        await getDownloadErrorMessage(err, 'Failed to download the report. Please try again later.')
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const handleView = async (reportId, reportName) => {
    try {
      setViewingId(reportId);
      const blob = await axios.get(`/api/investor/jvreports/view`, {
        params: { reportId },
        responseType: 'blob',
      });

      if (blob?.type === 'application/json') {
        const text = await blob.text();
        const result = JSON.parse(text);
        throw new Error(result?.error || result?.message || 'Failed to open report');
      }

      if (viewFileUrl) URL.revokeObjectURL(viewFileUrl);
      const url = URL.createObjectURL(blob);
      setViewFileUrl(url);
      setModalTitle(reportName || 'Document Preview');
      setIsModalOpen(true);
    } catch (err) {
      message.error(
        await getDownloadErrorMessage(err, 'Failed to open the report. Please try again later.')
      );
    } finally {
      setViewingId(null);
    }
  };

  const handleCloseModal = () => {
    if (viewFileUrl) URL.revokeObjectURL(viewFileUrl);
    setIsModalOpen(false);
    setViewFileUrl(null);
  };

  if (jvReportsLoading) {
    return (
      <div style={{ textAlign: "center", padding: 24 }}>
        <Spin />
      </div>
    );
  }

  if (!jvReports || jvReports.length === 0) {
    return null;
  }

  const panelStyle = { marginBottom: 10, border: "none" };

  const collapseItems = jvReports.map((group, idx) => ({
    key: String(idx + 1),
    label: (
      <Flex justify="space-between" align="center" style={{ width: "100%", paddingRight: 12 }}>
        <span>
          {group.dealName}&nbsp;
          <Badge
            count={group.reportCount}
            style={{ backgroundColor: "#d9d9d9", color: "#40566d", boxShadow: "none" }}
          />
        </span>
        <Text style={{ color: "#C4A35A", fontSize: 14, marginRight: 8 }}>
          Deal ID: {group.dealId}
        </Text>
      </Flex>
    ),
    children: (
      <div className="jv-report-list">
        {group.reports.map((report) => (
          <Flex
            key={report.reportId}
            justify="space-between"
            align="center"
            className="jv-report-row"
          >
            <Text>{report.reportName}</Text>
            <Space>
              <Button
                icon={<EyeOutlined />}
                loading={viewingId === report.reportId}
                onClick={() => handleView(report.reportId, report.reportName)}
                title="View Document"
                aria-label="View Document"
              />
              <Button
                type="primary"
                loading={downloadingId === report.reportId}
                onClick={() => handleDownload(report.reportId, report.reportName)}
              >
                Download
              </Button>
            </Space>
          </Flex>
        ))}
      </div>
    ),
    style: panelStyle,
  }));

  return (
    <div style={{ marginTop: 32 }}>
      <Title level={4}>JV Reports</Title>
      <div className="reports-details-container jv-reports-section">
        <Collapse
          bordered={false}
          items={collapseItems}
          expandIconPosition="end"
          expandIcon={({ isActive }) => (
            <Image
              src={ArrowDownIcon}
              alt="Arrow"
              preview={false}
              style={{
                transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
              }}
            />
          )}
          style={{ background: "#ffffff", width: "100%" }}
        />
      </div>
      <Modal
        open={isModalOpen}
        onCancel={handleCloseModal}
        footer={null}
        width={750}
        title={modalTitle || "Document Preview"}
        centered
        destroyOnClose
        styles={{ body: { paddingTop: 12 } }}
      >
        {viewFileUrl && (
          <iframe
            src={`${viewFileUrl}#toolbar=0&navpanes=0&scrollbar=0`}
            title={modalTitle || "JV Report"}
            width="100%"
            height="520px"
            style={{ border: 0 }}
          />
        )}
      </Modal>
    </div>
  );
};

const Reports = () => {
  const dispatch = useDispatch();

  const panelStyle = {
    marginBottom: 10,
    border: "none",
  };

  useEffect(() => {
    dispatch(getReportDropdowns());
    dispatch(getJvReports());
  }, [dispatch]);

  const collapseItems = (panelStyle) => [
    {
      key: "1",
      label: "Transaction Summary",
      children: <ReportsCollapseContent type="1" title="Transaction Summary" />,
      style: panelStyle,
    },
    {
      key: "2",
      label: "Deal Wise Profit Statement",
      children: <ReportsCollapseContent type="2" title="Deal Wise Profit Statement" />,
      style: panelStyle,
    },
  ];

  return (
    <div className='reports-container'>
      <Title level={4}>Download Reports</Title>
      <div className='reports-details-container'>
        <Collapse
          bordered={false}
          items={collapseItems(panelStyle)}
          expandIconPosition="end"
          expandIcon={({ isActive }) => (
            <Image
              src={ArrowDownIcon}
              alt='Arrow'
              preview={false}
              style={{
                transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease'
              }}
            />
          )}
          style={{ background: "#ffffff", width: "100%" }}
        />
      </div>
      <JvReportsSection />
    </div>
  );
}

export default Reports;
